import _ from 'lodash';

import {
  CLOSE_CHAT,
  NEW_MESSAGE,
  CHAT_ADD_USER,
  INLINE_UPDATE_JOB_NEEDS_ATTENTION_MESSAGE_COUNT,
  UNREDACTED_MESSAGE,
  CLEAR_UNREDACTED_MESSAGE,
  ENDUSER_JOINED_CHAT,
  CHAT_FORWARD_MEDIA,
  LOAD_ALL_CHAT_PARTICIPANTS,
  INLINE_UPDATE_JOB_NEEDS_ACTION_MESSAGE_COUNT,
  SNACKBAR_SHOW,
} from '../constants/actions';
import {
  REMOVE_USER_FROM_CHAT,
  ADD_USER_TO_CHAT,
  GET_CHAT_PARTICIPANTS,
  GET_ALL_CHAT_PARTICIPANTS,
  GET_UNREDACTED_MESSAGE,
  FORWARD_MEDIA,
  PUT_MESSAGE_NEEDS_ACTION_RESPONSE_ENDPOINT,
  PUT_MESSAGE_NEEDS_ATTENTION_RESPONSE_ENDPOINT,
  PATCH_JOB_ENDPOINT,
} from '../constants/endpoints';
import Message from '../models/message';
import User from '../models/user';
import Job from '../models/job';
import { BROADCAST_CHANNEL } from '../constants/general';
import { apiCall } from './api';
import { audioNotification } from './ui';
import { apiRequest, dispatchRequest } from './api/api';
import handleChatMessageItem from '@app/cache/chat-messages/chat-cache';
import { isInternalUnreadMessageSettingFlagOn } from '@app/constants/admin-config';
import { IGNORED_INTERNAL_SOURCES } from '@app/constants/messages';
import { getCreationSource } from '@app/helpers/creation-source';
import { showScheduledMessageCancelledToast } from '@app/helpers/scheduled-message-toasts';
import i18n from '@app/i18n';
import { describeError } from '@app/i18n/describe-error';

// Intentionally not imported from @app/services/admin to keep the main bundle size down
const INBOX_BEHAVIOR_UNREAD = 'unread';

export const closeChat = (chatId) => ({ type: CLOSE_CHAT, payload: chatId });

export const cancelScheduledMessage =
  ({ id, jobId, chatId }) =>
  async (dispatch, getState) => {
    // Check if there are any remaining scheduled messages for this chat BEFORE deleting
    // to avoid race conditions with the reducer update
    const state = getState();
    const remainingScheduledMessages = (
      state.jobs?.scheduledMessages || []
    ).filter((msg) => msg.chatId === chatId && msg.id !== id);

    // Cancel the scheduled message (proxied through chat server to event-scheduler Lambda)
    const result = await dispatchRequest(
      apiRequest.delete.message.queued({ chatId, messageId: id }),
      {
        type: 'CANCEL_SCHEDULED_MESSAGE',
        processPayload: (data) => ({ id: data.id }),
      },
    )(dispatch, getState);

    if (result) {
      showScheduledMessageCancelledToast();
    }

    // If no more scheduled messages remain, restore hasUnaddressedMessage flag
    if (remainingScheduledMessages.length === 0 && jobId) {
      const apiArgs = {
        endpoint: PATCH_JOB_ENDPOINT(jobId),
        method: 'PATCH',
        body: { hasUnaddressedMessage: true },
      };
      apiCall({ args: apiArgs })(dispatch);
    }

    return result;
  };

export const getScheduledMessages = (chatId) =>
  dispatchRequest(apiRequest.get.chat.scheduledMessages(chatId), {
    type: 'GET_SCHEDULED_MESSAGES',
    processPayload: (data) => data.filter((d) => !!d?.eventTime),
  });

export const setChatParticipants = ({ body, chatId }) => ({
  type: 'LOAD_CHAT_PARTICIPANTS',
  payload: {
    chatId,
    users: body.participants.map((u) => new User(u)),
  },
});

export const setChatParticipantsFromEvent = ({ chat }) => ({
  type: 'LOAD_CHAT_PARTICIPANTS',
  payload: {
    chatId: chat._id,
    users: chat.participants.map((u) => new User(u)),
  },
});

export const getChatParticipants = (chatId) => {
  const args = {
    endpoint: GET_CHAT_PARTICIPANTS(chatId),
    method: 'GET',
    action: 'LOAD_CHAT_PARTICIPANTS',
    process: ({ body }) => ({ users: body.map((u) => new User(u)), chatId }),
  };

  return apiCall({ args });
};

export const getAllChatParticipants = (chatId) => {
  const args = {
    endpoint: GET_ALL_CHAT_PARTICIPANTS(chatId),
    method: 'GET',
    action: 'LOAD_ALL_CHAT_PARTICIPANTS',
    process: ({ body }) => ({
      chatId,
      activeParticipants: body.participants?.map((u) => new User(u)) || [],
      pendingParticipants:
        body.pendingParticipants?.map((u) => new User(u)) || [],
      inactiveParticipants:
        body.inactiveParticipants?.map((u) => new User(u)) || [],
    }),
  };

  return apiCall({ args });
};

const shouldSuppressInternalMessages = (getState) => {
  if (!isInternalUnreadMessageSettingFlagOn()) return false;
  const state = getState();

  // Only suppress for Unread inboxes
  const inboxBehaviorSetting =
    state.organizations?.organizationSettings?.inboxBehavior;
  const orgBehaviorLockedByAdmin = inboxBehaviorSetting?.lockedByAdmin ?? true;
  const orgBehavior = inboxBehaviorSetting?.behavior ?? INBOX_BEHAVIOR_UNREAD;
  const userBehavior = state.profile?.properties?.inboxBehavior;
  const effectiveBehavior = orgBehaviorLockedByAdmin
    ? orgBehavior
    : (userBehavior ?? orgBehavior);
  if (effectiveBehavior !== INBOX_BEHAVIOR_UNREAD) return false;

  // Suppress if the internal unread message setting is enabled
  const orgSetting =
    state.organizations?.organizationSettings?.internalUnreadMessageSetting;
  const orgIgnore = orgSetting?.enabled ?? false;
  const lockedByAdmin = orgSetting?.lockedByAdmin ?? false;
  const userIgnore = state.profile?.properties?.ignoreInternalUnreadMessages;
  return lockedByAdmin ? orgIgnore : (userIgnore ?? orgIgnore);
};

export const newMessage = (data) => (dispatch, getState) => {
  const message = new Message(data);
  const currentUserId = _.get(getState(), 'auth.user._id');
  const authorId = _.get(data, 'authorId');
  const jobId = _.get(data, 'jobId');
  const isInbound = _.get(data, 'isInbound', false);
  const activeJobId = _.get(getState(), 'jobs.activeChat.jobId', '');
  const permissions = _.get(getState(), 'auth.permissions');

  const isOperatorOnJob =
    authorId !== currentUserId &&
    _.find(message.participants, (id) => id === currentUserId);

  if (isOperatorOnJob) {
    const shouldSuppressAudio =
      shouldSuppressInternalMessages(getState) &&
      data.source &&
      IGNORED_INTERNAL_SOURCES.includes(data.source);

    if (!shouldSuppressAudio) {
      dispatch(audioNotification());
    }
  }

  if (isInbound && activeJobId === jobId) {
    const channel = new BroadcastChannel(BROADCAST_CHANNEL);
    channel.postMessage({
      authId: currentUserId,
      jobId,
      messageId: message.id,
    });
  }
  const jobData = new Job(data.job);
  dispatch({
    type: NEW_MESSAGE,
    payload: {
      type: data.type,
      chatId: data.chatId,
      jobId: data.jobId,
      job: jobData,
      permissions,
      operatorId: currentUserId,
      message,
      currentUserId,
    },
  });
};

export function removeUserFromChat({ chatId, userId }) {
  const creationSource = getCreationSource();
  const args = {
    endpoint: REMOVE_USER_FROM_CHAT(chatId),
    method: 'PUT',
    action: 'CHAT_REMOVE_USER',
    body: { userId, creationSource },
  };

  return apiCall({ args });
}

/**
 * @param {object} params
 * @param {any} params.chatId
 * @param {any} params.userId
 * @param {boolean} [params.asSecondary]
 * @param {any} [params.toaster] Present only where the caller has one; falls back to the snackbar.
 */
export function addUserToChat({
  chatId,
  userId,
  asSecondary = false,
  toaster,
}) {
  const creationSource = getCreationSource();
  const args = {
    endpoint: ADD_USER_TO_CHAT(chatId),
    method: 'PUT',
    action: CHAT_ADD_USER,
    body: { userId, creationSource, asSecondary },
    // The server names the exposure types the user is missing; without this the refusal is silent.
    // Same two branches as jobPutRequest, so the control beside this one behaves identically.
    errorCallback: ({ dispatch, error }) => {
      const serverMessage = describeError(error, '');
      const title = i18n.t('toasts:case.updateFailedRetry');

      if (toaster) {
        toaster.create({
          title,
          type: 'error',
          description: serverMessage || undefined,
          duration: 5000,
        });
        return;
      }

      dispatch({
        type: SNACKBAR_SHOW,
        payload: {
          isError: true,
          text: serverMessage ? `${title} ${serverMessage}` : title,
        },
      });
    },
  };

  return apiCall({ args });
}

export const handleMessageNeedsActionResponse =
  (messageId, jobId, responseType, queryClient) => (dispatch, getStore) => {
    const args = {
      endpoint: PUT_MESSAGE_NEEDS_ACTION_RESPONSE_ENDPOINT(
        messageId,
        jobId,
        responseType,
      ),
      method: 'PUT',
      action: 'PUT_MESSAGE_NEEDS_ACTION_RESPONSE',
      callback: ({ body }) => {
        const store = getStore();
        const message = new Message(body?.message);
        handleChatMessageItem(message?.chatId, message, '', queryClient, store);
      },
    };
    return apiCall({ args })(dispatch, getStore);
  };

export const handleMessageNeedsAttentionResponse =
  (messageId, queryClient) => (dispatch, getStore) => {
    const args = {
      endpoint: PUT_MESSAGE_NEEDS_ATTENTION_RESPONSE_ENDPOINT(messageId),
      method: 'PUT',
      action: 'PUT_MESSAGE_NEEDS_ATTENTION_RESPONSE',
      callback: ({ body }) => {
        const store = getStore();
        const message = new Message(body?.message);
        handleChatMessageItem(message?.chatId, message, '', queryClient, store);
      },
    };
    return apiCall({ args })(dispatch, getStore);
  };

export const inlineMessageUpdate = (data) => (dispatch) => {
  dispatch({
    type: INLINE_UPDATE_JOB_NEEDS_ATTENTION_MESSAGE_COUNT,
    payload: {
      ...data,
      needsAttentionMessageCount: 1,
    },
  });
};

export const needsActionUpdate = (data) => (dispatch) => {
  dispatch({
    type: INLINE_UPDATE_JOB_NEEDS_ACTION_MESSAGE_COUNT,
    payload: {
      ...data,
    },
  });
};

export const getUnredactedMessage = (orgId, messageId, redactionType) => {
  const args = {
    endpoint: GET_UNREDACTED_MESSAGE(orgId, messageId, redactionType),
    method: 'GET',
    action: UNREDACTED_MESSAGE,
    process: ({ body }) => body,
  };

  return apiCall({ args });
};

export const clearUnredactedMessage = () => ({
  type: CLEAR_UNREDACTED_MESSAGE,
});

export const endUserJoinedChat = (data) => (dispatch) => {
  // Updates legacy chat in job reducer
  dispatch({
    type: ENDUSER_JOINED_CHAT,
    payload: data,
  });

  // Updates participants reducer for multi party chat
  if (data.chat) {
    dispatch({
      type: LOAD_ALL_CHAT_PARTICIPANTS,
      payload: {
        chatId: data.chat._id,
        activeParticipants:
          data.chat.participants?.map((u) => new User(u)) || [],
        pendingParticipants:
          data.chat.pendingParticipants?.map((u) => new User(u)) || [],
        inactiveParticipants:
          data.chat.inactiveParticipants?.map((u) => new User(u)) || [],
      },
    });
  }
};

export const forwardMedia = (chatId, messageId) => {
  const args = {
    endpoint: FORWARD_MEDIA(chatId),
    method: 'POST',
    action: CHAT_FORWARD_MEDIA,
    body: { messageId },
  };
  return apiCall({ args });
};
